"use client";

import React, { useEffect, useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, Clock, Rocket, PlayCircle, Zap, DollarSign, X, Trophy, Crown, Medal, TrendingUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import VideoCard from "../components/VideoCard";

// Nomes e países para gerar aleatoriamente
const FIRST_NAMES = [
  "James", "Mary", "John", "Anna", "David", "Sophia", "Peter", "Laura",
  "Michael", "Emma", "Joseph", "Olivia", "Louis", "Isabella", "Daniel",
  "Lucy", "Richard", "Mia", "Andrew", "Paula", "Gabriel", "Daniela",
];
const LAST_INITIALS = ["A", "B", "C", "D", "E", "F", "G", "H", "L", "M", "N", "P", "R", "S", "T", "V"];
const COUNTRIES = ["🇺🇸", "🇬🇧", "🇨🇦", "🇦🇺", "🇮🇪", "🇳🇿", "🇸🇬", "🇿🇦", "🇮🇳", "🇧🇷"];

// Função para gerar número "aleatório" baseado em seed (consistente por dia)
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Gerar Top 3 baseado na data (muda a cada dia, mas consistente durante o dia)
function generateDailyTop3() {
  const today = new Date();
  const dateSeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  
  const top3 = [];
  const usedNames = new Set();
  
  for (let i = 0; i < 3; i++) {
    // Gerar nome único
    let nameIndex;
    do {
      nameIndex = Math.floor(seededRandom(dateSeed + i * 100) * FIRST_NAMES.length);
    } while (usedNames.has(nameIndex));
    usedNames.add(nameIndex);
    
    const firstName = FIRST_NAMES[nameIndex];
    const lastInitial = LAST_INITIALS[Math.floor(seededRandom(dateSeed + i * 200) * LAST_INITIALS.length)];
    const country = COUNTRIES[Math.floor(seededRandom(dateSeed + i * 300) * COUNTRIES.length)];
    
    // Gerar ganhos (top 1: 2000-2500, top 2: 1800-2200, top 3: 1600-2000)
    const baseEarnings = [2000, 1800, 1600][i];
    const maxExtra = [500, 400, 400][i];
    const earnings = Math.floor(baseEarnings + seededRandom(dateSeed + i * 400) * maxExtra);
    
    top3.push({
      position: i + 1,
      name: `${firstName} ${lastInitial}.`,
      earnings,
      country,
    });
  }
  
  return top3;
}

function HomeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, buyerInfo, loading } = useAuth();
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showDailyRanking, setShowDailyRanking] = useState(false);

  // Pegar só o primeiro nome
  const firstName = (buyerInfo?.name || user?.user_metadata?.name || "")
    .split(" ")[0] || "Member";

  useEffect(() => {
    if (!loading && !user) {
      router.push("/login");
    }
  }, [loading, user, router]);

  // Verificar se é a primeira vez do usuário (por email)
  useEffect(() => {
    if (user?.email && !loading) {
      const welcomeKey = `mnc_welcome_seen_${user.email}`;
      const hasSeenWelcome = localStorage.getItem(welcomeKey);
      if (!hasSeenWelcome) {
        setShowWelcomePopup(true);
      }
    }
  }, [user, loading]);

  // Verificar popup diário de ranking
  useEffect(() => {
    if (user?.email && !loading && !showWelcomePopup) {
      const forceShow = searchParams.get("popup") === "ranking";
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const dailyKey = `mnc_daily_ranking_${user.email}`;
      const lastSeen = localStorage.getItem(dailyKey);

      // Mostrar se: forçado via URL OU (já viu boas-vindas E não viu ranking hoje)
      const welcomeKey = `mnc_welcome_seen_${user.email}`;
      const hasSeenWelcome = localStorage.getItem(welcomeKey);

      if (forceShow || (hasSeenWelcome && lastSeen !== today)) {
        setShowDailyRanking(true);
      }
    }
  }, [user, loading, showWelcomePopup, searchParams]);

  const handleCloseWelcome = () => {
    if (user?.email) {
      const welcomeKey = `mnc_welcome_seen_${user.email}`;
      localStorage.setItem(welcomeKey, "true");
      
      // Também marca que já viu o ranking hoje (para não aparecer no mesmo dia)
      const today = new Date().toISOString().split("T")[0];
      const dailyKey = `mnc_daily_ranking_${user.email}`;
      localStorage.setItem(dailyKey, today);
    }
    setShowWelcomePopup(false);
  };

  const handleCloseDailyRanking = () => {
    if (user?.email) {
      const today = new Date().toISOString().split("T")[0];
      const dailyKey = `mnc_daily_ranking_${user.email}`;
      localStorage.setItem(dailyKey, today);
    }
    setShowDailyRanking(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 pt-[70px] pb-[100px]"
      >
        <div className="max-w-md mx-auto px-4 py-6 space-y-6">
          {/* Welcome Section */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-2xl font-bold text-gray-900">
              You&apos;re in, {firstName}!
            </h1>
            <p className="text-gray-600 mt-2">
              Follow these steps to activate your AI and start trading:
            </p>
          </motion.div>

          {/* Video Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <VideoCard />
          </motion.div>
        </div>
      </motion.div>

      {/* Popup Diário - Top 3 do dia anterior */}
      <AnimatePresence>
        {showDailyRanking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden relative"
            >
              {/* Header com gradiente */}
              <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-5 text-center">
                <div className="flex justify-center mb-2">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                    <Trophy className="w-8 h-8 text-white drop-shadow-md" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-white drop-shadow-md">
                  Top users yesterday
                </h2>
                <p className="text-white/90 text-sm drop-shadow-sm">
                  See who earned the most yesterday
                </p>
              </div>

              {/* Botão fechar */}
              <button
                onClick={handleCloseDailyRanking}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center border border-white/30"
              >
                <X className="w-5 h-5 text-white drop-shadow-sm" />
              </button>

              {/* Content */}
              <div className="p-5">
                {/* Top 3 List */}
                <div className="space-y-3 mb-5">
                  {generateDailyTop3().map((trader, index) => (
                    <div
                      key={trader.position}
                      className={`flex items-center gap-3 p-3 rounded-xl ${
                        index === 0 
                          ? "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200" 
                          : "bg-gray-50 border border-gray-100"
                      }`}
                    >
                      {/* Position Icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        index === 0 
                          ? "bg-gradient-to-br from-amber-400 to-orange-500" 
                          : index === 1 
                            ? "bg-gradient-to-br from-gray-300 to-gray-400" 
                            : "bg-gradient-to-br from-amber-600 to-amber-700"
                      }`}>
                        {index === 0 ? (
                          <Crown className="w-5 h-5 text-white" />
                        ) : (
                          <Medal className="w-5 h-5 text-white" />
                        )}
                      </div>

                      {/* Name & Country */}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {trader.name} {trader.country}
                        </p>
                        <p className="text-xs text-gray-500">#{trader.position} of the day</p>
                      </div>

                      {/* Earnings */}
                      <div className="text-right">
                        <p className={`font-bold ${index === 0 ? "text-amber-600" : "text-green-600"}`}>
                          +${trader.earnings.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400">USD</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Motivational Message */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      <span className="font-semibold">You can too!</span> Generate signals today and show up in tomorrow&apos;s ranking.
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button
                  onClick={handleCloseDailyRanking}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                >
                  <Zap className="w-5 h-5" />
                  Start generating
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup de Boas-vindas - Primeira vez */}
      <AnimatePresence>
        {showWelcomePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden relative"
            >
              {/* Botão fechar */}
              <button
                onClick={handleCloseWelcome}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center transition-colors z-10"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>

              {/* Content */}
              <div className="p-6">
                {/* Title */}
                <h2 className="text-xl font-bold text-gray-900 text-center mb-4">
                  Welcome to<br />Money Club!
                </h2>
                {/* Description */}
                <div className="text-center mb-5">
                  <p className="text-gray-600 text-sm leading-relaxed">
                    This is <span className="font-semibold text-gray-800">YOUR APP</span> where you&apos;ll earn using <span className="font-semibold text-blue-600">Artificial Intelligence</span>.
                  </p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">COMPLETE THESE STEPS</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Steps */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 py-2 px-3 bg-blue-50/50 rounded-lg">
                    <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">1</span>
                    </div>
                    <span className="text-gray-700 text-sm flex-1">Watch all 5 lessons</span>
                    <span className="text-gray-400 text-xs">10 min</span>
                  </div>
                  
                  <div className="flex items-center gap-3 py-2 px-3 bg-orange-50/50 rounded-lg">
                    <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">2</span>
                    </div>
                    <span className="text-gray-700 text-sm flex-1">Use the Accelerator</span>
                    <span className="text-gray-400 text-xs">1 min</span>
                  </div>
                  
                  <div className="flex items-center gap-3 py-2 px-3 bg-green-50/50 rounded-lg">
                    <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">3</span>
                    </div>
                    <span className="text-gray-700 text-sm flex-1">Activate your Pocket</span>
                    <span className="text-gray-400 text-xs">5 min</span>
                  </div>
                </div>

                {/* Time estimate */}
                <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-5 bg-gray-50 py-2 rounded-lg">
                  <Clock className="w-4 h-4" />
                  <span>Total time: <span className="font-semibold text-gray-600">~15 minutes</span></span>
                </div>

                {/* CTA Button */}
                <motion.button
                  onClick={handleCloseWelcome}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  animate={{ 
                    scale: [1, 1.03, 1]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 text-base"
                >
                  <Rocket className="w-5 h-5" />
                  Start now
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Wrapper com Suspense para useSearchParams
export default function InicioPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    }>
      <HomeContent />
    </Suspense>
  );
}
